import { useEffect, useRef, type MutableRefObject } from "react";

/**
 * Tracks whether the user has actually EDITED the watched values.
 *
 * Autosave keys off this flag, so its arming rules are load-bearing:
 * - a plain mount must not arm it (arming on mount autosaved a junk
 *   untitled draft on every signed-in visit to `/`);
 * - values arriving while `hydrating` is true, and the render where
 *   hydration settles, must not arm it;
 * - only a change AFTER that arms it.
 *
 * The caller may reset `ref.current = false` (e.g. after a load) and the
 * next real change re-arms it. `watched` must keep a stable length.
 */
export function useEditTracking(
  hydrating: boolean,
  watched: readonly unknown[],
): MutableRefObject<boolean> {
  const editedRef = useRef(false);
  // "armed" = we have seen one settled render; later runs are real edits.
  const armedRef = useRef(false);

  useEffect(() => {
    if (hydrating) {
      armedRef.current = false;
      return;
    }
    if (!armedRef.current) {
      armedRef.current = true;
      return;
    }
    editedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hydrating, ...watched]);

  return editedRef;
}
